import { Box, useMediaQuery, useTheme } from "@mui/material";
import { UIEvent, useEffect, useRef, useState } from "react";
import { useConversation } from "../../../contexts/conversationContext";
import styles from "../../../pages/chat/Chat.module.css";
import { Answer, AnswerError } from "../../Chat/Answer";
import { UserChatMessage } from "../UserChatMessage";

type ChatTurnListProps = {
    retry: () => void;
};

/**
 * ChatTurnList renders a list of chat turns, including user messages, bot responses,
 * and handles various UI states such as loading, errors, and chat restart.
 *
 * @param {ChatTurnListProps} props - Properties to control the component's behavior.
 * @param {React.Ref<HTMLDivElement>} chatMessageStreamEnd - Ref forwarded to the bottom of the chat list for scrolling.
 * @returns {JSX.Element} Rendered chat turn list.
 */
const ChatTurnList = (props: ChatTurnListProps) => {
    const theme = useTheme();
    const min768 = useMediaQuery("(min-width:768px)");
    const { retry } = props;

    const { chatHistory } = useConversation();

    // Reference to the end of the chat message stream for auto-scrolling
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);

    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

        // only re-enable auto-scroll if user scrolled to bottom
        if (distanceFromBottom < 50) {
            if (!autoScrollEnabled) setAutoScrollEnabled(true);
        } else {
            if (autoScrollEnabled) setAutoScrollEnabled(false);
        }
    };

    // --- Detect latest streamed text and its completion status
    const latestAnswer = chatHistory.at(-1)?.response;
    const latestMessageText = latestAnswer?.answer ?? "";
    const isStreaming = latestAnswer?.responseStatus === "in_progress" || latestAnswer?.responseStatus === "queued";

    // --- Scroll logic
    useEffect(() => {
        if (!autoScrollEnabled || !chatMessageStreamEnd.current) return;

        const behavior = isStreaming ? "auto" : "smooth";
        chatMessageStreamEnd.current.scrollIntoView({ behavior });
    }, [latestMessageText, isStreaming, autoScrollEnabled]);

    return (
        <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className={styles.chatMessageStream}
            sx={{
                "&::-webkit-scrollbar": {
                    width: "8px",
                    backgroundColor: "transparent"
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.scrollbarThumb,
                    borderRadius: "4px",
                    "&:hover": {
                        backgroundColor: theme.palette.scrollbarThumb
                    }
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: theme.palette.scrollbarTrack,
                    borderRadius: "4px"
                },
                // For Firefox
                scrollbarWidth: "auto",
                scrollbarColor: `${theme.palette.scrollbarThumb} ${theme.palette.scrollbarTrack}`,
                paddingInline: 0
            }}
        >
            <Box sx={{ width: "90%", maxWidth: "1024px", minWidth: min768 ? "500px" : "300px" }}>
                {chatHistory.map((answer, index) => (
                    <Box key={index}>
                        <UserChatMessage message={answer.userMessage} />

                        {!answer.response.error && (
                            <Box sx={{ maxWidth: min768 ? "80%" : "85%", display: "flex", marginBottom: "20px" }}>
                                <Answer key={index} index={index} answer={answer.response} />
                            </Box>
                        )}

                        {answer.response.error ? (
                            <Box sx={{ maxWidth: "500px", marginBottom: "20px" }}>
                                <AnswerError error={answer.response.error} onRetry={retry} isLast={index === chatHistory.length - 1} />
                            </Box>
                        ) : null}
                    </Box>
                ))}
                <div style={{ height: "1px" }} ref={chatMessageStreamEnd} />
            </Box>
        </Box>
    );
};

export default ChatTurnList;
